import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import TabelaSistema, { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import {
  atualizarSettingPortal,
  CadastroDeCobranca,
  fetchCadastrosDeCobranca,
  fetchSettingsPortal,
  RegraDeCobranca,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import { notify } from '../../../shared/feedback/notify';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';

/**
 * Como cada forma de pagamento do fornecedor vira a COBRANÇA do pedido no Nasajon
 * (portal-cotacoes/at-15, decisão do Victor de 28/10/2026).
 *
 * Nenhuma opção ("Boleto 30 dias", "Pix / à vista") diz qual forma de pagamento e qual conta do
 * ERP usar, então a tradução é configurável e nasce vazia. Forma sem tradução não gera pedido: o
 * comprador vê a opção desabilitada na hora de gerar, em vez de o Portal inventar a cobrança.
 *
 * As listas de forma e conta vêm do próprio Nasajon e a tela guarda o id (o código da forma não é
 * único no ERP). Uma linha é ou vazia (não configurada) ou completa: meia regra não é gravada.
 */

const CHAVE_REGRAS = 'FORMAS_PAGAMENTO_ERP';
const CHAVE_OPCOES = 'FORMAS_PAGAMENTO_OPCOES';

interface Linha {
  opcao: string;
  /** A opção não está mais na lista oferecida ao fornecedor, mas proposta antiga pode tê-la. */
  foraDaLista: boolean;
  formaPagamento: string;
  conta: string;
  parcelas: string;
  diasPrimeiraParcela: string;
  intervaloDias: string;
}

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'Para que serve',
    conteudo: (
      <p>
        O pedido no Nasajon precisa de uma <strong>cobrança</strong>: forma de pagamento, conta,
        parcelas e vencimento. O fornecedor só marca opções como "Boleto 30 dias", e aqui cada
        opção é ligada ao que o ERP espera. Na hora de gerar o pedido, o comprador escolhe uma das
        formas que o fornecedor aceitou, e o Portal monta a cobrança por esta tabela.
      </p>
    ),
  },
  {
    titulo: 'Forma não configurada',
    conteudo: (
      <p>
        Enquanto uma linha estiver vazia, aquela forma <strong>não pode ser escolhida</strong> no
        pedido: ela aparece desabilitada para o comprador. O Portal não inventa forma nem conta,
        porque a cobrança vai direto para o financeiro.
      </p>
    ),
  },
  {
    titulo: 'Parcelas e vencimento',
    conteudo: (
      <p>
        O total do pedido é dividido igualmente entre as parcelas (os centavos que sobram vão na
        última). A 1ª parcela vence os <strong>dias até a 1ª parcela</strong> depois da emissão do
        pedido, e as seguintes a cada <strong>intervalo</strong>. Ex.: 3 parcelas, 30 dias e
        intervalo de 30 vencem em 30, 60 e 90 dias.
      </p>
    ),
  },
  {
    titulo: 'Vale daqui em diante',
    conteudo: (
      <p>
        Mudar uma linha vale para os pedidos gerados <strong>depois</strong> de salvar. Pedido já
        enviado ao ERP não muda.
      </p>
    ),
  },
];

const vazia = (opcao: string, foraDaLista: boolean): Linha => ({
  opcao,
  foraDaLista,
  formaPagamento: '',
  conta: '',
  parcelas: '',
  diasPrimeiraParcela: '',
  intervaloDias: '',
});

const preenchida = (l: Linha) =>
  Boolean(l.formaPagamento || l.conta || l.parcelas || l.diasPrimeiraParcela || l.intervaloDias);

const inteiro = (v: string, min: number, max: number) => {
  const n = Number(v);
  return v.trim() !== '' && Number.isInteger(n) && n >= min && n <= max ? n : null;
};

/** A regra da linha, ou `null` se ela estiver incompleta ou com número fora do limite. */
function regraDaLinha(l: Linha): RegraDeCobranca | null {
  const parcelas = inteiro(l.parcelas, 1, 24);
  const dias = inteiro(l.diasPrimeiraParcela, 0, 365);
  const intervalo = inteiro(l.intervaloDias, 1, 365);
  if (!l.formaPagamento || !l.conta || parcelas === null || dias === null || intervalo === null) return null;
  return { formaPagamento: l.formaPagamento, conta: l.conta, parcelas, diasPrimeiraParcela: dias, intervaloDias: intervalo };
}

const rotulo = (c: CadastroDeCobranca) => `${c.codigo} · ${c.descricao}`;

const CobrancaNoErpSection: React.FC<{ podeEditar: boolean }> = ({ podeEditar }) => {
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [formas, setFormas] = useState<CadastroDeCobranca[]>([]);
  const [contas, setContas] = useState<CadastroDeCobranca[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [ajudaAberta, setAjudaAberta] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [settings, cadastros] = await Promise.all([fetchSettingsPortal(), fetchCadastrosDeCobranca()]);
      const opcoes: string[] = JSON.parse(settings[CHAVE_OPCOES] || '[]');
      const regras: Record<string, RegraDeCobranca> = JSON.parse(settings[CHAVE_REGRAS] || '{}');
      const montar = (opcao: string, fora: boolean): Linha => {
        const r = regras[opcao];
        return r
          ? {
              opcao,
              foraDaLista: fora,
              formaPagamento: r.formaPagamento,
              conta: r.conta,
              parcelas: String(r.parcelas),
              diasPrimeiraParcela: String(r.diasPrimeiraParcela),
              intervaloDias: String(r.intervaloDias),
            }
          : vazia(opcao, fora);
      };
      setLinhas([
        ...opcoes.map((o) => montar(o, false)),
        ...Object.keys(regras)
          .filter((o) => !opcoes.includes(o))
          .map((o) => montar(o, true)),
      ]);
      setFormas(cadastros.formas);
      setContas(cadastros.contas);
      setErro(false);
    } catch (error) {
      // Tabela vazia por erro seria lida como "nada configurado", e salvar por cima apagaria a
      // configuração real (o salvamento é declarativo). Sem a carga, a tela não deixa editar.
      setLinhas([]);
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar a configuração da cobrança no Nasajon. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const alterar = useCallback((opcao: string, campo: keyof Linha, valor: string) => {
    setLinhas((atual) => atual.map((l) => (l.opcao === opcao ? { ...l, [campo]: valor } : l)));
  }, []);

  async function salvar() {
    const incompleta = linhas.find((l) => preenchida(l) && !regraDaLinha(l));
    if (incompleta) {
      notify.warning(
        `A linha "${incompleta.opcao}" está incompleta: preencha forma, conta, parcelas (1 a 24), dias até a 1ª parcela (0 a 365) e intervalo (1 a 365), ou limpe a linha.`,
      );
      return;
    }
    const regras: Record<string, RegraDeCobranca> = {};
    for (const l of linhas) {
      const r = regraDaLinha(l);
      if (r) regras[l.opcao] = r;
    }
    setSalvando(true);
    try {
      await atualizarSettingPortal(CHAVE_REGRAS, JSON.stringify(regras));
      notify.success('Configuração da cobrança salva com sucesso.');
      await carregar();
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível salvar a configuração da cobrança.');
    } finally {
      setSalvando(false);
    }
  }

  const nomeDe = useCallback(
    (lista: CadastroDeCobranca[], id: string) => {
      const c = lista.find((x) => x.id === id);
      return c ? rotulo(c) : id ? 'Não encontrada no Nasajon' : '';
    },
    [],
  );

  const colunas = useMemo<ColunaTabela<Linha>[]>(() => {
    const select = (l: Linha, campo: 'formaPagamento' | 'conta', lista: CadastroDeCobranca[]) =>
      podeEditar ? (
        <select
          className="admin-select"
          aria-label={`${campo === 'conta' ? 'Conta' : 'Forma de pagamento'} de ${l.opcao}`}
          value={l[campo]}
          disabled={salvando}
          onChange={(e) => alterar(l.opcao, campo, e.target.value)}
        >
          <option value="">Não configurada</option>
          {lista.map((c) => (
            <option key={c.id} value={c.id}>
              {rotulo(c)}
            </option>
          ))}
        </select>
      ) : (
        nomeDe(lista, l[campo]) || 'Não configurada'
      );
    const numero = (l: Linha, campo: 'parcelas' | 'diasPrimeiraParcela' | 'intervaloDias') =>
      podeEditar ? (
        <input
          className="admin-input"
          type="number"
          aria-label={`${campo} de ${l.opcao}`}
          style={{ width: 80 }}
          value={l[campo]}
          disabled={salvando}
          onChange={(e) => alterar(l.opcao, campo, e.target.value)}
        />
      ) : (
        l[campo] || '-'
      );
    return [
      {
        titulo: 'Forma do fornecedor',
        largura: 180,
        larguraMaxima: 240,
        texto: (l) => (l.foraDaLista ? `${l.opcao} (fora da lista atual)` : l.opcao),
      },
      { titulo: 'Forma no Nasajon', largura: 220, texto: (l) => nomeDe(formas, l.formaPagamento), render: (l) => select(l, 'formaPagamento', formas) },
      { titulo: 'Conta', largura: 240, texto: (l) => nomeDe(contas, l.conta), render: (l) => select(l, 'conta', contas) },
      { titulo: 'Parcelas', largura: 100, texto: (l) => l.parcelas, render: (l) => numero(l, 'parcelas') },
      { titulo: 'Dias até a 1ª', largura: 110, texto: (l) => l.diasPrimeiraParcela, render: (l) => numero(l, 'diasPrimeiraParcela') },
      { titulo: 'Intervalo (dias)', largura: 120, texto: (l) => l.intervaloDias, render: (l) => numero(l, 'intervaloDias') },
      {
        titulo: 'Situação',
        largura: 110,
        texto: (l) => (regraDaLinha(l) ? 'Pronta' : preenchida(l) ? 'Incompleta' : 'Não configurada'),
      },
    ];
  }, [podeEditar, salvando, formas, contas, alterar, nomeDe]);

  return (
    <>
      <div className="admin-card">
        <div className="admin-card-title">
          Cobrança no Nasajon
          <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setAjudaAberta(true)}>
            ?
          </button>
        </div>
        <p className="admin-hint" style={{ marginTop: 0 }}>
          Liga cada forma de pagamento do fornecedor à forma, à conta e às parcelas do pedido no
          Nasajon. Forma não configurada não pode ser escolhida na hora de gerar o pedido.
        </p>

        {erro ? (
          <p className="erro-de-tela">
            Não foi possível carregar a configuração ou as listas do Nasajon, então não dá para saber o
            que está configurado hoje. Recarregue a página antes de editar; se persistir, acione a TI.
          </p>
        ) : (
          <>
            <TabelaSistema
              colunas={colunas}
              itens={linhas}
              chave={(l) => l.opcao}
              vazio="Nenhuma forma de pagamento cadastrada."
              carregando={carregando}
            />
            {podeEditar && !carregando && (
              <div style={{ marginTop: 16 }}>
                <button type="button" className="admin-btn admin-btn-primary" disabled={salvando} onClick={salvar}>
                  {salvando ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <ComoFunciona
        isOpen={ajudaAberta}
        onClose={() => setAjudaAberta(false)}
        titulo="Como Funciona - Cobrança no Nasajon"
        secoes={helpSections}
      />
    </>
  );
};

export default CobrancaNoErpSection;
